import { PARTNER_PRIMARY_STATUSES } from '../Constants/partnerStatuses';

export const INFO_REQUESTED_STATUS = PARTNER_PRIMARY_STATUSES.INFO_REQUESTED;
export const AT_RISK_STATUS = PARTNER_PRIMARY_STATUSES.AT_RISK;
export const NEEDS_REVIEW_LABEL = 'Needs Review';

const PARTNER_SUBMIT_EXCLUDED_PROJECT_STATUSES = new Set([
  'Cancelled',
  NEEDS_REVIEW_LABEL,
]);

export const getSubmitProjectStatusOptions = (statusOptions = []) => (
  (statusOptions || []).filter(
    (status) => !PARTNER_SUBMIT_EXCLUDED_PROJECT_STATUSES.has(String(status || '').trim()),
  )
);

export const getActivityLog = (project) => (
  Array.isArray(project?.activityLog) ? project.activityLog : []
);

export const isSalesCycleStatus = (status) => (
  String(status || '').trim().startsWith('Sales Cycle')
);

export const hasPaeApproval = (project) => {
  if (typeof project?.paeApproved === 'boolean') {
    return project.paeApproved;
  }

  return getActivityLog(project).some(
    (entry) => String(entry?.action || '').trim().toLowerCase() === 'approved',
  );
};

export const shouldShowNeedsReview = (project) => (
  isSalesCycleStatus(project?.projectStatus) && !hasPaeApproval(project)
);

export const normalizeProjectStatus = (project) => project?.projectStatus;

export const getPartnerProjectStatusOptions = (formOptions = [], currentStatus = '') => {
  const editableStatuses = getSubmitProjectStatusOptions(formOptions);
  const uniqueStatuses = new Set(editableStatuses);
  const normalizedCurrent = String(currentStatus || '').trim();

  if (
    normalizedCurrent
    && !uniqueStatuses.has(normalizedCurrent)
    && !PARTNER_SUBMIT_EXCLUDED_PROJECT_STATUSES.has(normalizedCurrent)
  ) {
    return [normalizedCurrent, ...editableStatuses];
  }

  return editableStatuses;
};

export const getPaeModifyStatusOptions = (formOptions = [], currentStatus = '') => {
  const current = String(currentStatus || '').trim();
  const psmOnlyStatuses = new Set([AT_RISK_STATUS, INFO_REQUESTED_STATUS]);
  const options = (formOptions || []).filter((status) => !psmOnlyStatuses.has(status));

  if (!current) {
    return options;
  }

  return [current, ...options.filter((status) => status !== current)];
};

export const canPartnerChangeProjectStatus = (currentStatus) => (
  currentStatus === INFO_REQUESTED_STATUS
);

export const validatePartnerStatusUpdate = (form, previousStatus) => {
  if (
    previousStatus === INFO_REQUESTED_STATUS
    && form.projectStatus === INFO_REQUESTED_STATUS
  ) {
    return {
      isValid: false,
      errors: {
        projectStatus: 'Select a new project status after providing the requested information.',
      },
    };
  }

  return { isValid: true, errors: {} };
};

export const resolveUpdatedProjectStatus = (updatedProject, requestedStatus) => {
  if (!updatedProject) {
    return requestedStatus;
  }

  const responseStatus = updatedProject.projectStatus;
  if (
    responseStatus === INFO_REQUESTED_STATUS
    && requestedStatus
    && requestedStatus !== INFO_REQUESTED_STATUS
  ) {
    return requestedStatus;
  }

  return responseStatus || requestedStatus;
};

const normalizeStatusText = (value = '') => String(value || '').trim().toLowerCase();

const isGoLiveCompleteStatus = (status) => {
  const normalized = normalizeStatusText(status);
  return normalized.includes('go-live complete')
    || normalized.includes('go live complete')
    || normalized === 'golive complete';
};

const PAE_QUEUE_TABLE_PILL_CLASS = {
  'needs-review': 'is-needs-review',
  'at-risk': 'is-at-risk',
  'info-requested': 'is-info-requested',
  'go-live-complete': 'is-golive',
  completed: 'is-completed',
  active: 'is-active',
  'sales-cycle': 'is-sales-cycle',
  cancelled: 'is-cancelled',
};

/** Display-friendly line breaks at slashes (e.g. Sales Cycle / Not Started). */
export const formatPartnerTableStatusLabel = (label = '') => (
  String(label || '').trim().replace(/\s*\/\s*/g, ' / ')
);

const resolveMyProjectsStatusClassName = (raw) => {
  if (!raw || raw.toLowerCase() === 'unknown') {
    return 'is-empty';
  }
  if (raw === 'Active') {
    return 'is-active';
  }
  if (isSalesCycleStatus(raw)) {
    return normalizeStatusText(raw).includes('lost')
      ? 'is-sales-cycle is-sales-lost'
      : 'is-sales-cycle';
  }
  if (isGoLiveCompleteStatus(raw)) {
    return 'is-golive';
  }
  if (raw === 'Completed') {
    return 'is-completed';
  }
  if (raw === 'Info Requested') {
    return 'is-info-requested';
  }
  if (raw === 'At Risk') {
    return 'is-at-risk';
  }
  if (raw === 'Needs Review') {
    return 'is-needs-review';
  }
  if (raw === 'Cancelled') {
    return 'is-cancelled';
  }
  if (raw === 'Project On Hold' || raw === 'On Hold') {
    return 'is-on-hold';
  }
  return 'is-default';
};

export const getPartnerTableStatusMeta = ({ variant = 'myProjects', project, status } = {}) => {
  if (variant === 'paeQueue') {
    const displayLabel = getPaeQueueDisplayStatus(project);
    if (!displayLabel) {
      return { label: '', className: 'is-empty', title: '' };
    }

    const kebabClass = getPaeQueueStatusClassName(displayLabel);
    let className = PAE_QUEUE_TABLE_PILL_CLASS[kebabClass] || 'is-default';
    if (kebabClass === 'sales-cycle' && normalizeStatusText(displayLabel).includes('lost')) {
      className = 'is-sales-cycle is-sales-lost';
    }

    const fullStatus = String(project?.projectStatus || '').trim();
    const label = formatPartnerTableStatusLabel(displayLabel);
    const title = fullStatus && fullStatus !== displayLabel ? fullStatus : '';

    return { label, className, title };
  }

  const raw = String(status ?? project?.projectStatus ?? '').trim();
  if (!raw || raw.toLowerCase() === 'unknown') {
    return { label: '', className: 'is-empty', title: '' };
  }

  const className = resolveMyProjectsStatusClassName(raw);
  const label = formatPartnerTableStatusLabel(raw);
  const title = '';

  return { label, className, title };
};

export const getMyProjectTableStatusMeta = (status) => {
  const meta = getPartnerTableStatusMeta({ variant: 'myProjects', status });
  return { label: meta.label, className: meta.className, title: meta.title };
};

export const getPaeQueueTableStatusMeta = (project) => (
  getPartnerTableStatusMeta({ variant: 'paeQueue', project })
);

export const getPaeQueueDisplayStatus = (projectOrStatus) => {
  const project = typeof projectOrStatus === 'object' ? projectOrStatus : null;
  const status = normalizeProjectStatus(
    project || { projectStatus: projectOrStatus },
  );
  const normalizedStatus = normalizeStatusText(status);

  if (normalizedStatus.includes('at risk')) {
    return AT_RISK_STATUS;
  }
  if (normalizedStatus.includes('info requested')) {
    return INFO_REQUESTED_STATUS;
  }
  if (normalizedStatus.includes('active')) {
    return PARTNER_PRIMARY_STATUSES.ACTIVE;
  }
  if (
    normalizedStatus.includes('go-live complete')
    || normalizedStatus.includes('go live complete')
    || normalizedStatus.includes('golive complete')
  ) {
    return PARTNER_PRIMARY_STATUSES.GOLIVE;
  }
  if (normalizedStatus.includes('completed')) {
    return PARTNER_PRIMARY_STATUSES.COMPLETED;
  }
  if (isSalesCycleStatus(status)) {
    if (project && hasPaeApproval(project)) {
      return status;
    }
    if (!project) {
      return NEEDS_REVIEW_LABEL;
    }
    return shouldShowNeedsReview(project) ? NEEDS_REVIEW_LABEL : status;
  }

  return status || '';
};

export const getPaeQueueStatusClassName = (statusLabel) => {
  const label = String(statusLabel || '').trim();
  const normalizedLabel = normalizeStatusText(label);

  if (!label) {
    return '';
  }
  if (label === NEEDS_REVIEW_LABEL) {
    return 'needs-review';
  }
  if (normalizedLabel.includes('at risk')) {
    return 'at-risk';
  }
  if (normalizedLabel.includes('info requested')) {
    return 'info-requested';
  }
  if (normalizedLabel.includes('go-live complete')) {
    return 'go-live-complete';
  }
  if (normalizedLabel.includes('completed')) {
    return 'completed';
  }
  if (normalizedLabel.includes('cancelled')) {
    return 'cancelled';
  }
  if (normalizedLabel.includes('active')) {
    return 'active';
  }
  if (isSalesCycleStatus(label)) {
    return 'sales-cycle';
  }

  return 'needs-review';
};

export const getPaeQueueStatusBadgeMeta = (project) => {
  const label = getPaeQueueDisplayStatus(project);

  return {
    label,
    className: getPaeQueueStatusClassName(label),
  };
};
